// React
import { useMemo } from "react";
import { useParams } from "react-router-dom";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

// Components
import { DataTable } from "components/AtomsV2";
import Icon from "components/ShellV2/Icon";

// Redux
import { useGetTariffsByValidityQuery } from "state/erse/tariffsApiSlice";

// constants
import { getERSETypeLabelFromValue, getPeriodLabelFromValue } from "utils/constants/helpers";

function downloadTariffsCSV(data, validity) {
  const headers = [
    "t",
    "type",
    "ic_voltage_level",
    "ipr_voltage_level",
    "trimester",
    "period",
    "cieg_discount",
    "value",
  ];
  const csvRows = data.map((tariff) => [
    validity,
    tariff.type,
    tariff.ic_voltage_level,
    tariff.ipr_voltage_level ?? "",
    tariff.trimester ?? "",
    tariff.period ?? "",
    tariff.cieg_discount ?? "",
    tariff.value,
  ]);
  const csv = [headers, ...csvRows].map((r) => r.join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `tarifas_${validity}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

const emptyValue = "-";

const formatPercentage = (value) => (value != null ? `${value}%` : emptyValue);

const formatTrimester = (value) => (value != null ? value : emptyValue);

function formatTariffValue(value, tariff) {
  if (value == null) return emptyValue;

  const numericValue = Number(value);
  if (Number.isNaN(numericValue)) return value;

  const formatted = numericValue.toFixed(4);
  if (tariff.type_code === "ENERGY") return `${formatted} €/kWh`;
  if (tariff.type_code === "POWER") return `${formatted} €/kW/dia`;
  if (tariff.type_code === "CONTRACTED_POWER") {
    return tariff.ic_voltage_level?.includes("BTN")
      ? `${formatted} €/dia`
      : `${formatted} €/kW.dia`;
  }

  return formatted;
}

function TariffsTable({ validity: validityProp }) {
  const { validity: routeValidity } = useParams();
  const validity = validityProp ?? routeValidity;

  const { t } = useTranslation(["erse"]);

  // READ tariffs
  const { data } = useGetTariffsByValidityQuery(validity, { skip: !validity });

  const rows = useMemo(
    () =>
      (data ?? []).map((tariff, index) => {
        const ciegDiscount =
          tariff.cieg_discount != null && tariff.cieg_discount !== ""
            ? Number(tariff.cieg_discount)
            : null;
        const trimester = Number(tariff.trimester) > 0 ? Number(tariff.trimester) : null;

        return {
          id: `${tariff.type}-${tariff.ic_voltage_level}-${tariff.period ?? "no-period"}-${index}`,
          type: getERSETypeLabelFromValue(tariff.type),
          type_code: tariff.type,
          ic_voltage_level: tariff.ic_voltage_level,
          ipr_voltage_level: tariff.ipr_voltage_level || emptyValue,
          trimester,
          period: tariff.period ? getPeriodLabelFromValue(tariff.period) : emptyValue,
          cieg_discount: Number.isNaN(ciegDiscount) ? null : ciegDiscount,
          value: tariff.value,
        };
      }),
    [data]
  );

  const columns = [
    {
      key: "type",
      label: t("tables.tariffs.columns.type"),
      width: "16%",
      render: (value) => <span className="font-medium text-ink">{value}</span>,
    },
    {
      key: "ic_voltage_level",
      label: t("tables.tariffs.columns.ic_voltage_level"),
      width: "14%",
    },
    {
      key: "ipr_voltage_level",
      label: t("tables.tariffs.columns.ipr_voltage_level"),
      width: "14%",
    },
    {
      key: "trimester",
      label: t("tables.tariffs.columns.trimester"),
      align: "center",
      width: "10%",
      render: formatTrimester,
    },
    {
      key: "period",
      label: t("tables.tariffs.columns.period"),
      width: "12%",
    },
    {
      key: "cieg_discount",
      label: t("tables.tariffs.columns.cieg_discount"),
      align: "right",
      width: "12%",
      render: formatPercentage,
    },
    {
      key: "value",
      label: t("tables.tariffs.columns.value"),
      align: "right",
      render: (value, row) => (
        <span className="tabular-nums" style={{ color: "var(--accent)", fontWeight: 600 }}>
          {formatTariffValue(value, row)}
        </span>
      ),
    },
  ];

  const toolbarActions = (
    <button
      type="button"
      className="rec-btn-ghost rec-btn-sm"
      onClick={() => data && downloadTariffsCSV(data, validity)}
      disabled={!data || data.length === 0}
    >
      <Icon name="download" style={{ fontSize: 16 }} />
      {t("tables.tariffs.export_csv")}
    </button>
  );

  return (
    <DataTable
      columns={columns}
      rows={rows}
      maxRows={10}
      search={false}
      filters={toolbarActions}
      emptyText={t("list.empty")}
    />
  );
}

TariffsTable.propTypes = {
  validity: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

TariffsTable.defaultProps = {
  validity: null,
};

export default TariffsTable;
